import { useNavigate } from "react-router-dom";

interface BrandLogoProps {
  scrolled?: boolean;
  size?: "sm" | "md" | "lg";
  dark?: boolean;
}

export function BrandLogo({ scrolled = false, size = "md", dark = false }: BrandLogoProps) {
  const navigate = useNavigate();

  const sizeClasses = {
    sm: { mark: "w-6 h-6", text: "text-lg", tagline: "text-[9px]" },
    md: { mark: "w-8 h-8", text: "text-xl md:text-2xl", tagline: "text-[10px] md:text-xs" },
    lg: { mark: "w-10 h-10", text: "text-3xl md:text-4xl", tagline: "text-xs md:text-sm" },
  };

  const s = sizeClasses[size];
  const textColor = dark || scrolled ? "text-stone-900" : "text-white";
  const taglineColor = dark || scrolled ? "text-stone-500" : "text-white/60";
  const markBorder = dark || scrolled ? "border-stone-800" : "border-white/80";
  const markLine = dark || scrolled ? "bg-stone-800" : "bg-white/80";
  const markInner = dark || scrolled ? "border-stone-300/50" : "border-white/30";

  return (
    <button
      onClick={() => {
        navigate("/");
        window.scrollTo({ top: 0, behavior: "smooth" });
      }}
      className="flex items-center gap-3 cursor-pointer group"
    >
      {/* Geometric brand mark - diamond shape representing cut stone */}
      <div className={`relative ${s.mark} flex-shrink-0`}>
        <div className={`absolute inset-0 border ${markBorder} rotate-45 transition-colors group-hover:border-gold-500`} />
        <div className={`absolute inset-1.5 border ${markInner} rotate-45`} />
        <div className={`absolute top-1/2 left-0 right-0 h-[1px] ${markLine} -translate-y-1/2`} />
        <div className={`absolute top-0 bottom-0 left-1/2 w-[1px] ${markLine} -translate-x-1/2`} />
      </div>

      {/* Wordmark */}
      <div className="flex flex-col items-start">
        <span className={`font-serif ${s.text} font-semibold tracking-wide ${textColor} transition-colors`}>
          Lithos
          <span className="text-gold-500">.</span>
        </span>
        <span className={`${s.tagline} font-sans uppercase tracking-[0.25em] ${taglineColor} transition-colors hidden sm:inline`}>
          Stone of Distinction
        </span>
      </div>
    </button>
  );
}

export function BrandMark({ className = "" }: { className?: string }) {
  return (
    <div className={`relative w-12 h-12 flex-shrink-0 ${className}`}>
      <div className="absolute inset-0 border border-gold-500 rotate-45" />
      <div className="absolute inset-2 border border-gold-300/50 rotate-45" />
      <div className="absolute top-1/2 left-0 right-0 h-[1px] bg-gold-500 -translate-y-1/2" />
      <div className="absolute top-0 bottom-0 left-1/2 w-[1px] bg-gold-500 -translate-x-1/2" />
    </div>
  );
}

export function BrandPattern({ className = "" }: { className?: string }) {
  return (
    <div className={`absolute inset-0 opacity-[0.03] pointer-events-none ${className}`}>
      <div
        className="w-full h-full"
        style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 20px,
            currentColor 20px,
            currentColor 21px
          ),
          repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 20px,
            currentColor 20px,
            currentColor 21px
          )`,
        }}
      />
    </div>
  );
}